import { h, replaceChildren } from '@/components/dom';
import { button, note, numberField, row, section, setButtonContent } from '@/components/controls';
import { icons, svgIcon } from '@/components/icons';
import type { App } from './App';
import { isContinuous } from '@/state/commands';
import { sequenceView } from '@/state/appState';

export function createEditPanel(app: App): HTMLElement {
  const selectedInfo = h('div', { class: 'info-box' }, 'Click a string on the canvas to select it.');
  const del = button({ label: 'Delete', icon: icons.trash, variant: 'danger', title: 'Delete the selected string (Del)', onClick: () => void app.deleteSelected() });
  const up = button({ label: 'Earlier', icon: icons.up, title: 'Move the selected string one step earlier', onClick: () => void app.moveSelected(-1) });
  const down = button({ label: 'Later', icon: icons.down, title: 'Move the selected string one step later', onClick: () => void app.moveSelected(1) });
  const lock = button({ label: 'Lock', icon: icons.lock, title: 'Locked strings survive regeneration and truncation', onClick: () => app.state.ui.selected !== null && app.toggleLock(app.state.ui.selected) });
  const regen = button({ label: 'Regenerate after selection', variant: 'primary', icon: icons.wand, title: 'Keep strings up to the selection (plus locked ones) and continue optimizing', onClick: () => app.state.ui.selected !== null && void app.regenerateFrom(app.state.ui.selected + 1) });
  const regenPlayhead = button({ label: 'Regenerate from playhead', onClick: () => void app.regenerateFrom(app.state.playback.index) });
  const truncate = button({ label: 'Truncate to playhead', variant: 'danger', onClick: () => void app.truncateToPlayhead() });

  const from = numberField({ label: 'From pin', value: 0, min: 0, step: 1, onChange: () => {} });
  const to = numberField({ label: 'To pin', value: 1, min: 0, step: 1, onChange: () => {} });
  const fromInput = from.element.querySelector('input')!;
  const toInput = to.element.querySelector('input')!;
  const add = button({ label: 'Add string', icon: icons.plus, onClick: () => void app.addManualString(Number(fromInput.value), Number(toInput.value)) });
  const addTool = button({ label: 'Pick pins on canvas', variant: 'ghost', onClick: () => app.updateUi({ tool: app.state.ui.tool === 'addString' ? 'select' : 'addString', pendingPin: null }) });
  const undo = button({ label: 'Undo', icon: icons.undo, onClick: () => void app.undo() });
  const redo = button({ label: 'Redo', icon: icons.redo, onClick: () => void app.redo() });
  const list = h('div', { class: 'string-list', attrs: { role: 'list' } });
  const continuity = h('p', { class: 'ctl-note' });

  app.store.subscribe((s, p) => {
    const sel = s.ui.selected;
    const changed = s.ui.selected !== p.ui.selected || s.solution !== p.solution || s.history !== p.history || s.status !== p.status || s.ui.tool !== p.ui.tool || s.ui.pendingPin !== p.ui.pendingPin || s === p;
    if (!changed) return;
    const seq = sequenceView(s.solution);
    const busy = s.status === 'running' || s.status === 'preparing' || s.status === 'loading';
    const has = sel !== null && sel < s.solution.count;
    if (has) {
      const a = seq[sel * 2];
      const b = seq[sel * 2 + 1];
      const score = s.solution.scores[sel];
      const error = s.solution.errors[sel];
      selectedInfo.textContent = `String ${sel + 1} / ${s.solution.count}: pin ${a} → ${b}` + (Number.isFinite(score) && sel < s.solution.scores.length ? `, score ${score.toFixed(5)}` : '') + (Number.isFinite(error) && sel < s.solution.errors.length ? `, error ${error.toFixed(4)}` : '') + (s.solution.locked.has(sel) ? ', locked' : '');
      const locked = s.solution.locked.has(sel);
      setButtonContent(lock, locked ? 'Unlock' : 'Lock', locked ? icons.unlock : icons.lock);
    } else {
      selectedInfo.textContent = s.solution.count === 0 ? 'No strings yet.' : 'Click a string on the canvas to select it.';
      setButtonContent(lock, 'Lock', icons.lock);
    }
    for (const b of [del, up, down, lock, regen]) b.disabled = !has || busy;
    regenPlayhead.disabled = busy || s.target.width === 0;
    truncate.disabled = busy || s.solution.count === 0;
    add.disabled = busy || s.solution.pins.length === 0;
    undo.disabled = !s.history.canUndo || busy;
    redo.disabled = !s.history.canRedo || busy;
    addTool.classList.toggle('is-active', s.ui.tool === 'addString');
    setButtonContent(addTool, s.ui.tool === 'addString' ? (s.ui.pendingPin !== null ? `Pin ${s.ui.pendingPin} picked — click the second pin` : 'Click the first pin… (Esc to stop)') : 'Pick pins on canvas', icons.crosshair);
    continuity.textContent = s.solution.count > 0 ? (isContinuous(seq) ? 'The sequence is one continuous thread.' : 'The sequence contains thread breaks (independent strings).') : '';
    const center = has ? sel : Math.max(0, s.solution.count - 1);
    const start = Math.max(0, center - 8);
    const end = Math.min(s.solution.count, start + 17);
    replaceChildren(
      list,
      ...Array.from({ length: end - start }, (_, k) => {
        const i = start + k;
        const locked = s.solution.locked.has(i);
        const rowEl = h(
          'button',
          { type: 'button', class: `string-row${i === sel ? ' is-selected' : ''}${locked ? ' is-locked' : ''}`, attrs: { role: 'listitem' } },
          h('span', { class: 'string-index' }, String(i + 1)),
          h('span', { class: 'string-pins' }, `pin ${seq[i * 2]} → ${seq[i * 2 + 1]}`),
          locked ? h('span', { class: 'string-lock', title: 'Locked' }, svgIcon(icons.lock)) : null,
        );
        rowEl.addEventListener('click', () => {
          app.selectString(i);
          app.playback.seek(Math.max(i + 1, s.playback.index));
        });
        return rowEl;
      }),
    );
  }, { immediate: true });

  return h(
    'div',
    null,
    section('Selection', [selectedInfo, row(del, up, down, lock), regen, note('Selection follows clicks on the canvas; use Delete / arrow keys for quick edits.')]),
    section('Strings', [list, continuity]),
    section('Manual string', [row(from.element, to.element), row(add, addTool)]),
    section('Regenerate', [row(regenPlayhead, truncate), note('Locked strings are kept when regenerating or truncating.')]),
    section('History', [row(undo, redo)]),
  );
}
